// AuthContext: estado global de autenticación.
//
// Persistencia:
//   - access_token   → localStorage 'idoyoga_auth_token'
//   - refresh_token  → localStorage 'idoyoga_refresh_token'
//   - perfil         → en memoria (no se cachea para evitar staleness)
//
// Al montar: si hay token, llama /api/auth/me para rehidratar.
// Si /me responde 401, limpia tokens (token expirado o revocado).
//
// El access_token de Supabase JWT dura 1h por defecto. Para esta sesión
// no implementamos refresh automático — si el token expira mientras el
// usuario está activo, /api/auth/me devolverá 401 y se le pedirá relogin.
// El refresh sí lo guardamos para poder añadir refresh-flow más adelante.

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API_BASE, ApiError } from '../lib/api';

const TOKEN_KEY = 'idoyoga_auth_token';
const REFRESH_KEY = 'idoyoga_refresh_token';

const AuthContext = createContext(null);

function readToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
}
function writeTokens(access, refresh) {
  try {
    if (access) localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch (_) {}
}
function clearTokens() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch (_) {}
}

// Helper interno: fetch con Authorization si hay token. Devuelve body parseado
// y throws ApiError si no-OK.
async function authFetch(path, opts = {}) {
  const token = readToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const r = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  let body = null;
  try { body = await r.json(); } catch (_) {}
  if (!r.ok) throw new ApiError(r.status, body?.code, body?.error);
  return body;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);  // true mientras rehidratamos al montar
  const [error, setError] = useState(null);

  // Rehidratación al montar
  useEffect(() => {
    const token = readToken();
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await authFetch('/api/auth/me');
        if (!cancelled) setUser(me);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          // Token caducado/revocado — limpiar
          clearTokens();
        } else {
          // Error de red u otro: mantenemos el token, mostraremos error
          // pero no impedimos seguir navegando.
          console.warn('[auth] rehidratación falló:', err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- API pública del context ----

  const signup = useCallback(async (payload) => {
    setError(null);
    const data = await authFetch('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    // Si Confirm Email está OFF en Supabase, signup devuelve session inmediata.
    if (data.access_token) {
      writeTokens(data.access_token, data.refresh_token);
      setUser(data.user);
    }
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    const data = await authFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    writeTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authFetch('/api/auth/logout', { method: 'POST' });
    } catch (_) { /* logout server-side es best-effort */ }
    clearTokens();
    setUser(null);
  }, []);

  const updateMyProfile = useCallback(async (changes) => {
    setError(null);
    const data = await authFetch('/api/auth/me', {
      method: 'PUT',
      body: JSON.stringify(changes),
    });
    setUser((prev) => ({ ...(prev || {}), ...data }));
    return data;
  }, []);

  const changeMyPassword = useCallback(async ({ password_actual, password_nueva }) => {
    setError(null);
    return await authFetch('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ password_actual, password_nueva }),
    });
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    setError(null);
    return await authFetch('/api/auth/request-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }, []);

  const resetPassword = useCallback(async ({ access_token, password }) => {
    setError(null);
    return await authFetch('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ access_token, password }),
    });
  }, []);

  const refreshUser = useCallback(async () => {
    if (!readToken()) {
      setUser(null);
      return null;
    }
    try {
      const me = await authFetch('/api/auth/me');
      setUser(me);
      return me;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        clearTokens();
        setUser(null);
      }
      throw err;
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    signup,
    login,
    logout,
    refreshUser,
    updateMyProfile,
    changeMyPassword,
    requestPasswordReset,
    resetPassword,
    setError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() debe usarse dentro de <AuthProvider>');
  return ctx;
}

// Helper exportado para que api.js u otros módulos puedan hacer fetch autenticado
// sin pasar por el context (p.ej. /mis-reservas).
export { authFetch, readToken, clearTokens };
